import { Vector3 } from 'three/webgpu';
import { flags } from '../core/flags';

export const G0 = 9.81;

/** Scales every hollow's default gravity (`?gravity=0.5` gives the lighter worlds of before). */
const GRAVITY_SCALE = flags.num('gravity', 1);

/**
 * Default surface gravity for a hollow of radius R (metres). With a single rock
 * density, gravity would grow linearly with R. Instead we keep the physically
 * correct *shape* of the field but use a gentle law, so every size stays playable:
 * s = 2 g * (R / 10 km)^0.15, clamped to [0.6 g, 2.6 g]. Heavy worlds for a hero who
 * jumps high (see PlayerController).
 */
export function defaultSurfaceGravity(radius: number): number {
  const g = 2 * G0 * (radius / 10_000) ** 0.15;
  return Math.min(Math.max(g, 0.6 * G0), 2.6 * G0) * GRAVITY_SCALE;
}

export interface SunDef {
  /** Radius of the sun body in metres. */
  radius: number;
  /** Linear RGB colour of the emitted light at "noon". */
  color: [number, number, number];
  /** Irradiance delivered to the hollow wall at noon (renderer units). */
  intensity: number;
}

export interface HollowDef {
  id: string;
  name: string;
  /** Universe-space centre, metres (f64). */
  center: Vector3;
  /** Reference wall radius in metres; terrain displaces inward/outward from it. */
  radius: number;
  /** Surface gravity strength at the wall, m/s². */
  gravity: number;
  seed: number;
  sun: SunDef | null;
}

export function makeHollow(
  partial: Omit<HollowDef, 'gravity' | 'center'> & { center: [number, number, number]; gravity?: number },
): HollowDef {
  return {
    ...partial,
    center: new Vector3(...partial.center),
    gravity: partial.gravity ?? defaultSurfaceGravity(partial.radius),
  };
}
